import { Input, Modal, Form as FormAnt, Upload, message, Select, Switch, Row, Col } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useMutation } from 'react-query';
import { sdk } from '../../../../../../API/TT_ELD/mobile/sdk';

const { Dragger } = Upload;
const { TextArea } = Input;

const AddModelList = ({
  open,
  setOpen,
  dataRefetch,
}: {
  open: boolean;
  setOpen(open: boolean): void;
  dataRefetch(): void;
}) => {
  const [form] = FormAnt.useForm();

  const handleCancel = () => {
    setOpen(!open);
  };

  const normFile = (e: any) => (Array.isArray(e) ? e : e?.fileList);

  const beforeUpload = (file: File) => {
    const isBin = file.name.toLowerCase().endsWith('.bin');
    if (!isBin) {
      message.error('You can only upload .BIN files!');
    }
    const isLt200M = file.size / 1024 / 1024 < 200;
    if (!isLt200M) {
      message.error('File must be smaller than 200MB!');
    }
    return isBin && isLt200M ? false : Upload.LIST_IGNORE;
  };

  const onPost = useMutation(({ formData }: { formData: FormData }) => sdk.createSDK(formData), {
    onSuccess: (res: any) => {
      message.success('Upload successful!');
      form.resetFields();
      setOpen(false);
      dataRefetch();
    },
    onError: (err: any) => {
      message.error(err?.response?.data?.message || 'Upload failed!');
    },
  });
  const onFinish = async () => {
    try {
      const values = await form.validateFields();

      const file = values.file?.[0]?.originFileObj;
      if (!file) {
        message.error('No file selected!');
        return;
      }

      if (!file.name.toLowerCase().endsWith('.bin')) {
        message.error('File must have a .bin extension!');
        return;
      }

      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('type', values.type);
      formData.append('is_supported', values.is_supported);
      formData.append('is_active', values.is_active);
      formData.append('file', file);
      if (values.note?.trim()) {
        formData.append('note', values.note.trim());
      }

      await onPost.mutateAsync({ formData });
    } catch (err) {
      console.error(err);
      message.error('Validation failed or upload error');
    }
  };

  return (
    <>
      <Modal
        loading={onPost?.isLoading}
        confirmLoading={onPost?.isLoading}
        open={open}
        title="Create a new Bluetooth"
        okText="Create"
        cancelText="Cancel"
        onCancel={handleCancel}
        onOk={onFinish}
      >
        <FormAnt
          form={form}
          layout="vertical"
          name="form_in_modal"
          initialValues={{ modifier: 'public', type: 'ble', is_active: true, is_supported: true }}
        >
          <FormAnt.Item
            label="Name"
            name="name"
            rules={[
              {
                required: true,
                message: 'Please enter the name!',
              },
              {
                validator: async (_, value) => {
                  if (value && value.trim() === '') {
                    throw new Error('Name cannot be empty!');
                  }
                },
              },
            ]}
          >
            <Input placeholder="Enter name" />
          </FormAnt.Item>

          <FormAnt.Item
            label="Type"
            name="type"
            rules={[{ required: true, message: 'Please select the type!' }]}
          >
            <Select
              showSearch
              placeholder="Select a type"
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label as string).toLowerCase().includes(input.toLowerCase())
              }
              options={[{ label: 'BLE', value: 'ble' }]}
            />
          </FormAnt.Item>

          <FormAnt.Item label="Note" name="note">
            <TextArea rows={4} />
          </FormAnt.Item>

          <Row gutter={16}>
            <Col span={12}>
              <FormAnt.Item label="Active" name="is_active" valuePropName="checked">
                <Switch />
              </FormAnt.Item>
            </Col>

            <Col span={12}>
              <FormAnt.Item label="Supported" name="is_supported" valuePropName="checked">
                <Switch />
              </FormAnt.Item>
            </Col>
          </Row>

          <FormAnt.Item label="Dragger">
            <FormAnt.Item
              name="file"
              valuePropName="fileList"
              getValueFromEvent={normFile}
              noStyle
              rules={[{ required: true, message: 'Please upload a .BIN file!' }]}
            >
              <Dragger
                name="file"
                accept=".bin"
                beforeUpload={beforeUpload}
                multiple={false}
                maxCount={1}
                onChange={(info) => {
                  const { fileList } = info;
                  if (fileList.length > 1) {
                    fileList.splice(1);
                    message.warning('You can only upload one file.');
                  }
                }}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">Click or drag .BIN file to this area to upload</p>
                <p className="ant-upload-hint">Only one .BIN file is allowed.</p>
              </Dragger>
            </FormAnt.Item>
          </FormAnt.Item>
        </FormAnt>
      </Modal>
    </>
  );
};

export default AddModelList;
